import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { projects } from "@/data/projects";
import { projectThumbs } from "@/data/thumbs";
import { useI18n } from "@/lib/i18n";
import { translatePortuguese } from "@/data/portuguese-core";

/** Ordered slugs for the home rail (matches the artist's selection). */
const railSlugs = [
  "what-is-madness",
  "the-weight-of-feeling",
  "apophenia-and-pareidolia",
  "camara-obscura",
  "ecologies-of-perception",
  "garden-of-dreams-and-nightmares-situated-between-the-loom",
  "butoh-caipira",
  "colonized-sexuality",
  "diary-of-memories-that-i-dont-have",
  "topographies-of-violence",
  "discourses-of-necropolitics",
];

const railTitles: Record<string, string> = {
  "ecologies-of-perception": "Ecologies of Perception",
};

/** Custom backdrop images for specific projects. */
const railCovers: Record<string, string> = projectThumbs;

/** Break a title into two balanced lines. */
function twoLines(title: string) {
  const words = title.split(" ");
  if (words.length < 2) return [title];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}

export function ProjectsRail() {
  const { lang } = useI18n();
  const items = railSlugs
    .map((slug) => projects.find((p) => p.slug === slug))
    .filter((project): project is (typeof projects)[number] => Boolean(project));

  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const section = sectionRef.current;
      const track = trackRef.current;
      if (!section || !track) return;
      const rect = section.getBoundingClientRect();
      const scrollable = section.offsetHeight - window.innerHeight;
      const p = scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 0;
      setProgress(p);
      const distance = Math.max(0, track.scrollWidth - window.innerWidth);
      track.style.transform = `translate3d(${-p * distance}px,0,0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [items.length]);

  const active = Math.min(items.length - 1, Math.round(progress * (items.length - 1)));

  return (
    <section
      id="work"
      ref={sectionRef}
      className="relative z-10 bg-ink"
      style={{ height: `${items.length * 62}vh` }}
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* parallax backdrop reveal */}
        <div className="pointer-events-none absolute inset-0">
          {items.map((p, i) => (
            <img
              key={p.slug}
              src={railCovers[p.slug] ?? p.cover}
              alt={`${p.title} — Luiza Jesus do Prado`}
              aria-hidden
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ease-out ${
                i === active ? "opacity-70" : "opacity-0"
              }`}
              style={{
                transform: `scale(${1.06 + progress * 0.02})`,
                maskImage:
                  "linear-gradient(to bottom, transparent 0%, #000 12%, #000 88%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent 0%, #000 12%, #000 88%, transparent 100%)",
              }}
            />
          ))}
          <div className="absolute inset-0 bg-ink/55" />

        </div>

        <div className="pointer-events-none absolute left-6 top-[14vh] z-10 md:left-14">
          <span className="font-mono text-[9px] uppercase tracking-[0.45em] text-bone/65">
            {translatePortuguese("Selected projects", lang)}
          </span>
        </div>

        <div className="absolute inset-0 flex items-center">
          <div
            ref={trackRef}
            className="flex h-full items-center will-change-transform"
          >
            {items.map((p, i) => (
              <Link
                key={p.slug}
                to="/work/$slug"
                params={{ slug: p.slug }}
                className="group flex h-full w-screen shrink-0 flex-col justify-center px-[10vw]"
              >
                <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-bone/65">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2
                  className={`mt-5 whitespace-nowrap font-didone text-[clamp(2.2rem,4.8vw,4.6rem)] font-light leading-[1] transition-colors duration-700 ${
                    i === active ? "text-bone" : "text-bone/65 group-hover:text-bone/70"
                  }`}
                >
                  {twoLines(translatePortuguese(railTitles[p.slug] ?? p.title, lang)).map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </h2>
                <span className="mt-5 block font-mono text-[9px] uppercase tracking-[0.35em] text-bone/65">
                  {p.year}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
